import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import CertifiedBadge from '../components/CertifiedBadge'
import { EMAIL_SPAM_NOTE } from '../config/support'
import './Page.css'
import './Docs.css'

function Docs() {
  const { pathname, hash } = useLocation()

  // Fragment-only links like href="#section" resolve against <base href="/"> and send
  // users to /#section (home). Use /docs#... + scroll after navigation.
  // Collapsible sections use <details>; if we scroll while closed, only the summary is
  // in view and the next open section (e.g. Legal) fills the screen — open first, then scroll.
  useEffect(() => {
    if (pathname !== '/docs' || !hash || hash.length < 2) return
    const id = decodeURIComponent(hash.slice(1))
    const el = document.getElementById(id)
    if (!el) return

    const details = el.querySelector(':scope > details')
    if (details) {
      details.open = true
    }

    const scrollToSection = () => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToSection)
    })
  }, [pathname, hash])

  return (
    <div className="page-container docs-page">
      <div className="docs-layout">
        <aside className="docs-sidebar" aria-label="Documentation navigation">
          <h2 className="docs-sidebar-title">Alchemy Docs</h2>
          <nav className="docs-nav">
            <Link to="/docs#overview" className="docs-nav-link">
              Overview
            </Link>
            <Link to="/docs#early-adopters" className="docs-nav-link">
              Founders program
            </Link>
            <Link to="/docs#verified-users" className="docs-nav-link">
              Verified users
            </Link>
            <Link to="/docs#legal" className="docs-nav-link">
              Legal &amp; DMCA
            </Link>
            <Link to="/docs#source-files" className="docs-nav-link">
              Source files
            </Link>
            <Link to="/docs#roll-back" className="docs-nav-link">
              Roll back
            </Link>
            <Link to="/docs#provenance" className="docs-nav-link">
              Provenance &amp; Reverse Engineering
            </Link>
            <Link to="/docs#moderation" className="docs-nav-link">
              Moderation &amp; Enforcement
            </Link>
          </nav>
        </aside>

        <div className="docs-main">
          <header className="docs-header">
            <h1>Alchemy Forum Documentation and Guidelines</h1>
            <p>
              High‑level guides on how Alchemy Forum works, how to contribute safely, and
              how we handle legal and moderation concerns.
            </p>
          </header>

          <section id="overview" className="docs-section">
            <h2>What Alchemy Forum Is</h2>
            <p>
              Alchemy Forum is a community archive for independently produced schematics,
              notes, and documentation created through lawful reverse engineering and
              measurement. It is <strong>not</strong> a mirror of proprietary service
              manuals or factory documentation.
            </p>
          </section>

          <section id="early-adopters" className="docs-section">
            <details className="docs-collapsible">
              <summary className="docs-collapsible-summary">Founders and early adopters</summary>
              <div className="docs-collapsible-body">
                <p>
                  Founders and early adopter information has moved to the dedicated{' '}
                  <Link to="/founders">Wall of Founders page</Link> so users can access it
                  directly from the main navigation.
                </p>
                <p>
                  Legal and moderation policy remain in this documentation page.
                </p>
              </div>
            </details>
          </section>

          <section id="verified-users" className="docs-section">
            <details className="docs-collapsible">
              <summary className="docs-collapsible-summary">Verified users and checkmarks</summary>
              <div className="docs-collapsible-body">
                <p>
                  Trusted identities use small badges next to usernames: a{' '}
                  <strong>gold star-in-ring</strong> for founding contributors and a{' '}
                  <strong>blue checkmark</strong> for forum verification.{' '}
                  <strong>Founders always show both</strong>, because founding status includes
                  automatic verification.
                </p>
                <p className="docs-verified-example">
                  Example — verified only (not in founders program): <strong>username</strong>
                  <CertifiedBadge
                    user={{ forum_certified: true, is_founding_contributor: false }}
                    title="Verified on Alchemy Forum"
                  />
                </p>
                <p className="docs-verified-example">
                  Example — founding contributor (star + check): <strong>username</strong>
                  <CertifiedBadge
                    user={{ forum_certified: true, is_founding_contributor: true }}
                    title="Founding contributor and verified"
                  />
                </p>
                <h3>Who is verified</h3>
                <ul>
                  <li>
                    <strong>Members of the founding program</strong> are verified
                    automatically and keep that status indefinitely.
                  </li>
                  <li>
                    <strong>Admins</strong> can manually award or remove verified status
                    from the Admin Dashboard.
                    <ul>
                      <li>
                        <strong>As of now:</strong> there is no public criteria-based path
                        to become verified outside the founders program.
                      </li>
                      <li>
                        <strong>Future plans:</strong> non-founder users may become
                        verified by additional criteria and methods as the platform
                        evolves.
                      </li>
                    </ul>
                  </li>
                </ul>
                <h3>What the checkmark means</h3>
                <p>
                  The checkmark indicates the account is recognized by the platform as a
                  verified contributor identity. It does not imply legal endorsement,
                  perfect technical accuracy, or moderation immunity.
                </p>
                <h3>Where you will see it</h3>
                <ul>
                  <li>On profile usernames.</li>
                  <li>Next to repository owner names.</li>
                  <li>
                    Next to branch and pull request authors where creator identity is
                    shown.
                  </li>
                </ul>
              </div>
            </details>
          </section>

          <section id="legal" className="docs-section">
            <details className="docs-collapsible">
              <summary className="docs-collapsible-summary">
                Legal &amp; DMCA Policy
              </summary>
              <div className="docs-collapsible-body">
                <h3>Hard Bans on Uploaded Content</h3>
                <p>
                  To respect copyright and keep the platform sustainable, the following
                  content is <strong>not allowed</strong>:
                </p>
                <ul>
                  <li>
                    No scans, photos, or verbatim transcriptions of copyrighted manuals.
                  </li>
                  <li>
                    No traced schematics that are directly derived from proprietary
                    documents.
                  </li>
                  <li>
                    No uploads labeled &quot;official,&quot; &quot;factory,&quot; or &quot;service manual&quot; unless you
                    are the rights holder and clearly state that in the notes.
                  </li>
                </ul>
                <p>
                  Every contribution that adds or updates schematics or notes requires you
                  to confirm that the work is your original creation or derived from
                  lawful reverse engineering. If you are unsure whether something is
                  allowed, do not upload it.
                </p>

                <h3>Contributor Attestation</h3>
                <p>
                  When you submit a new commit, circuit notes, or a new repository, you
                  must check a box stating:
                </p>
                <blockquote>
                  &ldquo;This submission is my original work or derived from lawful reverse
                  engineering, and does not include scans, photos, or transcriptions of
                  copyrighted manuals, traced schematics from proprietary documents, or
                  any files labeled &apos;official,&apos; &apos;factory,&apos; or &apos;service
                  manual.&apos;&rdquo;
                </blockquote>
                <p>
                  Submissions that violate this policy may be removed, and repeat or
                  bad‑faith violations may lead to account restrictions.
                </p>

                <h3>How We Keep Uploads Legally Safer</h3>
                <p>
                  Alchemy Forum is designed so that legal expectations are visible and
                  enforced in multiple places, not just in fine print:
                </p>
                <ul>
                  <li>
                    <strong>Built‑in hard bans</strong> – The contribution flow explicitly
                    bans uploads of scans/photos/transcriptions of manuals, traced
                    proprietary schematics, and &quot;official/factory/service manual&quot;
                    files.
                  </li>
                  <li>
                    <strong>Mandatory legal checkbox in the form</strong> – Before you
                    can submit a new repository, commit, or circuit notes, you must
                    actively confirm that your upload is original work or the result of
                    lawful reverse engineering and does not violate the hard bans above.
                  </li>
                  <li>
                    <strong>Provenance guidance</strong> – Documentation strongly
                    encourages you to record how a schematic was produced, which tools
                    were used, and how confident you are in the result, so that readers
                    can distinguish careful reverse engineering from copied documentation.
                  </li>
                  <li>
                    <strong>Owner‑controlled repositories</strong> – Repository owners and
                    admins approve or reject contributions to their projects, which helps
                    filter out questionable uploads before they become part of the
                    canonical history.
                  </li>
                  <li>
                    <strong>Separation from official manuals</strong> – The platform is
                    intentionally focused on community‑generated reverse‑engineered
                    schematics and notes, not on hosting factory service material.
                  </li>
                  <li>
                    <strong>Operations safeguards</strong> – The backend enforces rate
                    limits, security headers, and basic abuse protections, and there is a
                    published DMCA contact for rights holders to request removal of
                    infringing material.
                  </li>
                </ul>
                <p>
                  These measures are meant to give contributors clear expectations and
                  give rights holders a straightforward way to raise concerns. They are
                  not a substitute for your own legal advice, but they show how Alchemy
                  Forum is structured around lawful reverse engineering and respect for
                  copyright.
                </p>

                <h3>DMCA &amp; Takedowns</h3>
                <p>
                  If you are a rights holder and believe that content hosted on Alchemy
                  Forum infringes your copyright, you can request removal. We aim to
                  respond quickly and handle disputes in good faith.
                </p>
                <p>
                  For now, DMCA and copyright complaints can be sent to the site operator
                  at our support address:
                </p>
                <p>
                  <code>support@alchemyelectronics.com</code>
                </p>
                <p>{EMAIL_SPAM_NOTE}</p>
                <p>Please include, at a minimum:</p>
                <ul>
                  <li>Your full name and contact information.</li>
                  <li>
                    A description of the copyrighted work you believe has been infringed
                    (links to official sources if available).
                  </li>
                  <li>
                    A direct link to the specific page, repository, or file on Alchemy
                    Forum that you are reporting.
                  </li>
                  <li>
                    A statement that you have a good‑faith belief that the use is not
                    authorized by the copyright owner, its agent, or the law.
                  </li>
                  <li>
                    A statement, under penalty of perjury, that the information you
                    provide is accurate and that you are the copyright owner or
                    authorized to act on the owner&apos;s behalf.
                  </li>
                </ul>
                <p>
                  As the site grows, a formally registered DMCA agent and dedicated
                  policy page may be added; this page will be updated when that happens.
                </p>

                <h3>DMCA registered agent (when applicable)</h3>
                <p>
                  When the project formally registers a DMCA designated agent (e.g. with
                  the U.S. Copyright Office), the agent name, mailing address, and
                  contact details will be published here and in the site footer. Until
                  then, use the contact above (<code>support@alchemyelectronics.com</code>) for
                  takedown requests.
                </p>

                <h3>Internal takedown workflow (for admins)</h3>
                <p>
                  When a DMCA or copyright complaint is received, follow this workflow
                  for a consistent, documented response:
                </p>
                <ol>
                  <li>
                    <strong>Record</strong> – Log the notice (date received, claimant
                    name/contact, target URL or repository/file, brief summary). Keep a
                    copy of the full notice.
                  </li>
                  <li>
                    <strong>Verify</strong> – Confirm the reported content exists and
                    matches the notice. If applicable, note any counter-notice or
                    fair-use context before deciding.
                  </li>
                  <li>
                    <strong>Act</strong> – Remove or hide the reported content as
                    appropriate (e.g. reject or remove the contribution, hide the
                    repository, or remove the specific file). Record the action (e.g. in
                    an internal moderation log) for accountability.
                  </li>
                  <li>
                    <strong>Respond</strong> – Reply to the claimant confirming receipt
                    and that the content was removed (or explaining why not). Retain
                    copies of the notice and your response for legal records.
                  </li>
                </ol>
                <p>
                  This workflow is for internal use; a moderation audit log, when
                  available, provides an accountable record of takedown-related actions.
                </p>
              </div>
            </details>
          </section>

          <section id="source-files" className="docs-section">
            <details className="docs-collapsible">
              <summary className="docs-collapsible-summary">Source files (CAD &amp; editable schematics)</summary>
              <div className="docs-collapsible-body">
                <p>
                  In addition to schematic images or PDFs and circuit notes, repositories can
                  include <strong>source files</strong> — editable CAD projects and related
                  assets stored under <code>source/</code> in the repo tree.
                </p>
                <p>
                  <strong>KiCad files are the currently official supported source package.</strong>{' '}
                  When contributing, upload KiCad project files such as{' '}
                  <code>.kicad_sch</code>, <code>.kicad_pro</code>, and related KiCad assets.
                  More supported source types are on the way.
                </p>
                <p>
                  Source files are versioned with commits and branches like other repository
                  content. You can add a short description per file when uploading. Pull requests
                  show when source files were added, changed, or removed.
                </p>
              </div>
            </details>
          </section>

          <section id="roll-back" className="docs-section">
            <details className="docs-collapsible">
              <summary className="docs-collapsible-summary">Roll back</summary>
              <div className="docs-collapsible-body">
                <p>
                  Repository owners and site admins can undo the latest commit on{' '}
                  <strong>main</strong>, one commit at a time. The control is on the
                  repository page, next to commit history. The first commit on a
                  repository cannot be rolled back.
                </p>
                <p>
                  Roll back always moves <strong>main</strong> to the previous commit.
                  What happens to the undone work depends on how that commit landed.
                </p>
                <h3>Merged pull request</h3>
                <p>
                  If the latest commit came from a merged pull request, that commit
                  leaves <strong>main</strong>. Its branch is restored, and the pull
                  request is opened again so it can be reviewed and merged. The
                  contribution returns to pending.
                </p>
                <h3>Direct commit</h3>
                <p>
                  If a repository owner added the commit straight to <strong>main</strong>{' '}
                  — no separate branch and no pull request — roll back only moves{' '}
                  <strong>main</strong> back. No new branch or pull request is created.
                </p>
                <h3>Commit history</h3>
                <p>
                  History keeps the full record: the original commit, the merge if there
                  was one, and the rollback. A rollback entry names the commit{' '}
                  <strong>main</strong> returned to, including its short hash and message.
                  The commit that was removed stays in the history.
                </p>
              </div>
            </details>
          </section>

          <section id="provenance" className="docs-section">
            <details className="docs-collapsible">
              <summary className="docs-collapsible-summary">
                Provenance &amp; Reverse Engineering Notes
              </summary>
              <div className="docs-collapsible-body">
                <p>
                  For each schematic or technical document, contributors are strongly
                  encouraged to record how the work was produced. This provenance
                  information helps other users understand how trustworthy and complete a
                  schematic is.
                </p>
                <p>You should document, in your notes or accompanying files:</p>
                <ul>
                  <li>
                    <strong>How it was produced</strong> – e.g. measurement, teardown,
                    logic analysis, tracing a PCB, firmware disassembly.
                  </li>
                  <li>
                    <strong>Tools used</strong> – e.g. oscilloscope, DMM, logic analyzer,
                    VNA, JTAG/SWD debugger.
                  </li>
                  <li>
                    <strong>Confidence level</strong> – how certain you are that the
                    schematic matches the actual hardware or behavior.
                  </li>
                  <li>
                    <strong>Known differences from any originals</strong> – corrections,
                    simplifications, or intentional modifications.
                  </li>
                </ul>
                <p>
                  Where practical, include this context directly in your circuit notes
                  files (under <code>notes/</code>) so that future readers have the full
                  story of how the schematic was derived.
                </p>
              </div>
            </details>
          </section>

          <section id="moderation" className="docs-section">
            <details className="docs-collapsible">
              <summary className="docs-collapsible-summary">
                Moderation &amp; Enforcement
              </summary>
              <div className="docs-collapsible-body">
                <h3>Reporting content</h3>
                <p>
                  All content flags and inquiries go through the website first and are
                  sent to our support address. To report a repository, file, or commit:
                </p>
                <ul>
                  <li>
                    Use the <strong>Report content</strong> button on the repository
                    page (in the header), or the <strong>Report</strong> link next to a
                    specific file (in the schematic files list) or next to a commit (in
                    commit history).
                  </li>
                  <li>
                    Choose a reason (e.g. copyright/DMCA concern, prohibited content,
                    other) and add any optional details. Submitting opens your email
                    client with a pre-filled message to our support address.
                  </li>
                  <li>
                    We review all reports and may request take downs on content or take
                    other action (e.g. removal, restrictions) as appropriate. Reports
                    are sent to <code>support@alchemyelectronics.com</code>. {EMAIL_SPAM_NOTE} For formal DMCA or
                    copyright claims, see the Legal &amp; DMCA section above; you can
                    use the same address for both general content reports and DMCA
                    notices.
                  </li>
                </ul>
                <p>
                  Alchemy Forum uses a combination of human review and tooling to keep
                  the archive healthy:
                </p>
                <ul>
                  <li>
                    Community flagging for potentially infringing or inappropriate
                    uploads, with a review process that leads to actual decisions.
                  </li>
                  <li>
                    Preference for trusted and experienced contributors when reviewing
                    sensitive uploads.
                  </li>
                  <li>
                    Internal audit logs for moderation actions (edits, removals,
                    approvals) to preserve accountability.
                  </li>
                </ul>
                <p>
                  Moderation tooling will continue to evolve, but the core principle is
                  simple: preserve legitimate reverse‑engineered knowledge, and remove
                  material that puts the project or contributors at legal risk.
                </p>
                <p>
                  To protect the project and its community, we reserve the right to{' '}
                  <strong>remove or restrict content and accounts at any time at our sole
                  discretion</strong>, particularly in cases involving suspected
                  infringement, abuse, spam, or other behavior that threatens the safety,
                  legality, or integrity of the platform.
                </p>
              </div>
            </details>
          </section>
        </div>
      </div>
    </div>
  )
}

export default Docs

